import React from 'react';
import { DataInputConfig, DatasetType } from '@/types/automationFields';

import { Dataset } from '@/types/datasets.ts';

// Option shape used by your selects
export type Option = { value: string; label: string; leftSection?: React.ReactNode };
export type OptionGroup = { group: string; items: Option[] };

// Canonical builders (kept public so option construction stays consistent)
export const makeStaticOptionValue = (dataset_id: string) => `static ${dataset_id}`;
export const makeLinkedOptionValue = (dataset_id: string) => `linked ${dataset_id}`;
export const makeDynamicOptionValue = (stepId: string, output_id?: string | null) =>
  output_id ? `dynamic ${stepId} ${output_id}` : `dynamic ${stepId}`;
export const makeEmptyOptionValue = (datasetType: DatasetType) => `empty ${datasetType}`;

// If a dynamic value includes an output id, normalize down to the step-scoped key
export const normalizeDynamicValue = (value: string) => {
  if (!value.startsWith('dynamic')) return value;
  const [, stepId] = value.split(' ');
  return makeDynamicOptionValue(stepId);
};

// Decorate a flat options array with icons from the context
export function withIcons(
  options: Option[],
  getIconForOptionValue: (value: string) => React.ReactNode | null,
): Option[] {
  return options.map((o) => ({ ...o, leftSection: getIconForOptionValue(o.value) ?? undefined }));
}

// Decorate grouped options (matches your hook output)
export function withIconsForGroups(
  groups: OptionGroup[],
  getIconForOptionValue: (value: string) => React.ReactNode | null,
): OptionGroup[] {
  return groups.map((g) => ({ group: g.group, items: withIcons(g.items, getIconForOptionValue) }));
}

// Fake dataset for EMPTY entries so we can reuse datasetToFileTypeAndIcon
export function fakeDataset(name: string, dataset_type: DatasetType): Dataset {
  return {
    id: `fake-${dataset_type}`,
    name,
    dataset_type,
    processing: false,
    blob_path: '',
    has_original_blob: false,
    original_blob_path: '',
    is_output_dataset: false,
    has_parent: false,
  } as unknown as Dataset;
}

// Get an icon directly from a DataInputConfig via the context's getter
export function getIconForConfigFactory(
  getIconForOptionValue: (value: string) => React.ReactNode | null,
) {
  return (config: DataInputConfig) => {
    if ('static_dataset_id' in config && config.static_dataset_id) {
      return getIconForOptionValue(makeStaticOptionValue(config.static_dataset_id));
    } else if ('linked_dataset_id' in config && config.linked_dataset_id) {
      return getIconForOptionValue(makeLinkedOptionValue(config.linked_dataset_id));
    } else if ('source_step_id' in config && config.source_step_id) {
      return getIconForOptionValue(
        makeDynamicOptionValue(config.source_step_id, (config as any).output_id),
      );
    } else if ('dataset_type' in config && config.dataset_type) {
      return getIconForOptionValue(makeEmptyOptionValue(config.dataset_type));
    }
    return null;
  };
}
